// Date/time helpers — the platform's storage format is always UTC. The
// backend persists MySQL DATETIME columns through Sequelize's default
// `timezone: '+00:00'` and serialises them as ISO-8601 with a `Z` suffix
// ("2027-12-31T00:00:00.000Z"), so every timestamp arriving from the API
// is UTC regardless of where the server runs.
//
// The UI renders in whatever zone the admin picked in Global Settings, so
// every timestamp on screen must round-trip through this module.
//
// `useTimezone` is the React entry point — it subscribes to the
// global-settings cache so a save in the Global Settings form re-renders
// every consumer immediately. Calling it anywhere in a page also re-renders
// that page's subtree, which picks up the plain formatters below.

import { useEffect, useState } from 'react'

import moment from 'moment-timezone'

import { globalSettingsStorage, subscribeCache } from './globalSettingsStorage'

export const DATE_FORMAT = 'DD/MM/YYYY'
export const TIME_FORMAT = 'HH:mm'
export const DATE_TIME_FORMAT = 'DD/MM/YYYY HH:mm'
export const LONG_DATE_FORMAT = 'DD/MM/YYYY'
export const TIME_WITH_SECONDS_FORMAT = 'HH:mm:ss'

// Read the stored zone synchronously. Defaults to UTC so consumers that
// mount before ProductSelection has populated the cache — or that hit a
// zone this moment-timezone build doesn't know — still get a sane value
// instead of an "Invalid date".
export const getStoredTimezone = () => {
  const cached = globalSettingsStorage.getCached()?.defaultTimezone
  return cached && moment.tz.zone(cached) ? cached : 'UTC'
}

// Formats accepted from the API. ISO-8601 with a `Z` is what the backend
// actually sends; the two naive variants are here so a bare timestamp is
// read as UTC rather than browser-local.
//
// Parsing is strict against this list: anything else yields null instead of
// falling through to `new Date(...)`, which moment warns about and which
// parses inconsistently across browsers.
const ACCEPTED_FORMATS = [moment.ISO_8601, 'YYYY-MM-DD HH:mm:ss', 'YYYY-MM-DD']

// Parse an incoming API value as UTC. Accepts a string in one of the
// formats above, a JS Date, an epoch-milliseconds number, or a moment.
//
// Returns null (never a moment) for anything else, including `{}` and `[]`
// — `moment({})` is *now*, so a malformed payload would otherwise render as
// today's date and look like real data.
const toMoment = (value) => {
  if (value === null || value === undefined || value === '') return null

  if (typeof value === 'string') {
    const parsed = moment.utc(value, ACCEPTED_FORMATS, true)
    return parsed.isValid() ? parsed : null
  }

  const isSupported = value instanceof Date || moment.isMoment(value) || Number.isFinite(value)
  if (!isSupported) return null

  const parsed = moment.utc(value)
  return parsed.isValid() ? parsed : null
}

// Convert a UTC value into the active display zone. Returns a moment (so
// callers can chain .format/.fromNow/.diff) or null.
export const inTimezone = (value, timezone = getStoredTimezone()) => {
  const parsed = toMoment(value)
  return parsed ? parsed.tz(timezone) : null
}

// The workhorse. `fallback` defaults to an empty string rather than a dash
// so existing `|| PLACEHOLDER` guards at the call sites keep working.
export const formatInTimezone = (value, format = DATE_TIME_FORMAT, fallback = '') => {
  const converted = inTimezone(value)
  return converted ? converted.format(format) : fallback
}

export const formatDateOnly = (value, fallback = '') =>
  formatInTimezone(value, DATE_FORMAT, fallback)

export const formatDateTime = (value, fallback = '') =>
  formatInTimezone(value, DATE_TIME_FORMAT, fallback)

export const formatTimeOnly = (value, fallback = '') =>
  formatInTimezone(value, TIME_FORMAT, fallback)

export const formatLongDate = (value, fallback = '') =>
  formatInTimezone(value, LONG_DATE_FORMAT, fallback)

// Split rendering for tables that show the date and the time in separate
// columns (activity log). Both halves share one conversion so they can
// never disagree across a midnight boundary.
export const formatDateTimeParts = (value, fallback = '—') => {
  const converted = inTimezone(value)
  if (!converted) return { d: fallback, t: fallback }
  return {
    d: converted.format(LONG_DATE_FORMAT),
    t: converted.format(TIME_WITH_SECONDS_FORMAT),
  }
}

export const getRelativeTime = (value, fallback = '—') => {
  const converted = inTimezone(value)
  return converted ? converted.fromNow() : fallback
}

// True when the value parses. Use this to guard before formatting, rather
// than relying on a truthiness check on the formatted output.
export const isValidDate = (value) => toMoment(value) !== null

// True when the value is strictly in the past. Null/unparseable → false.
export const isPast = (value) => {
  const parsed = toMoment(value)
  return parsed ? parsed.isBefore(moment()) : false
}

// Signed distance from now, in whole units. Positive = the value is in the
// future. Truncates toward zero the same way `moment().diff()` does, so
// pair it with `isPast` when you need to distinguish "expired 12 hours ago"
// (which is 0 days, not -1) from "expires in 12 hours".
//
// Replaces the `moment(x).diff(moment(), 'days')` pattern that was repeated
// across the device-management tables and detail pages.
export const diffFromNow = (value, unit = 'days') => {
  const converted = inTimezone(value)
  return converted ? converted.diff(moment(), unit) : null
}

// Write path — take the wall-clock value a date picker produced, read it as
// a time in the display zone, and return the UTC ISO string the API expects.
//
// A picker hands back a JS Date pinned to the *browser's* zone, so passing
// it straight to `moment.tz` would keep the instant and merely relabel it.
// Normalising to a wall-clock string first makes "31 Dec, midnight" mean
// midnight in the display zone, which is what the user picked.
//
// Not yet wired into the forms; the pickers still submit browser-local values.
export const toUtcISO = (value, timezone = getStoredTimezone()) => {
  if (value === null || value === undefined || value === '') return null
  const wallClock = value instanceof Date ? moment(value).format('YYYY-MM-DDTHH:mm:ss') : value
  const parsed = moment.tz(wallClock, timezone)
  return parsed.isValid() ? parsed.toISOString() : null
}

// React hook — returns the active timezone, refreshed when the cache
// changes (Global Settings save, login prefetch).
export function useTimezone() {
  const [timezone, setTimezone] = useState(getStoredTimezone)
  useEffect(() => subscribeCache(() => setTimezone(getStoredTimezone())), [])
  return timezone
}
